/**
 * @module services/citationService
 * @description Citation formatting service using citeproc-js
 *
 * Provides citation formatting using CSL styles for WYSIWYG preview.
 * Uses citeproc-js to format citations identically to Pandoc's --citeproc.
 */

import { createLogger } from './loggerService';
import type { ZoteroItem } from './zoteroService';

const log = createLogger('citation');

// =============================================================================
// TYPES
// =============================================================================

/** CSL-JSON item format (used by citeproc-js) */
export interface CSLItem {
  id: string;
  type: string;
  title?: string;
  author?: CSLName[];
  editor?: CSLName[];
  translator?: CSLName[];
  issued?: CSLDate;
  'container-title'?: string;
  publisher?: string;
  'publisher-place'?: string;
  page?: string;
  'number-of-pages'?: string;
  volume?: string;
  issue?: string;
  edition?: string;
  'collection-title'?: string;
  'collection-number'?: string;
  ISBN?: string;
  ISSN?: string;
  DOI?: string;
  URL?: string;
  abstract?: string;
  language?: string;
  [key: string]: unknown;
}

/** CSL name format */
export interface CSLName {
  family?: string;
  given?: string;
  literal?: string;
  'non-dropping-particle'?: string;
  'dropping-particle'?: string;
  suffix?: string;
}

/** CSL date format */
export interface CSLDate {
  'date-parts'?: number[][];
  raw?: string;
  literal?: string;
}

/** Citation cluster for processCitationCluster */
export interface CitationCluster {
  citationItems: Array<{
    id: string;
    locator?: string;
    label?: string;
    prefix?: string;
    suffix?: string;
  }>;
  properties: {
    noteIndex: number;
  };
}

/** Formatted citation result */
export interface FormattedCitation {
  id: string;
  text: string;
  html: string;
}

// =============================================================================
// CSL LOCALES (embedded minimal en-US locale)
// =============================================================================

const EN_US_LOCALE = `<?xml version="1.0" encoding="utf-8"?>
<locale xmlns="http://purl.org/net/xbiblio/csl" version="1.0" xml:lang="en-US">
  <info>
    <rights license="http://creativecommons.org/licenses/by-sa/3.0/">CC BY-SA 3.0</rights>
    <updated>2023-01-01T00:00:00+00:00</updated>
  </info>
  <style-options punctuation-in-quote="true"/>
  <date form="text">
    <date-part name="month" suffix=" "/>
    <date-part name="day" suffix=", "/>
    <date-part name="year"/>
  </date>
  <date form="numeric">
    <date-part name="month" form="numeric-leading-zeros" suffix="/"/>
    <date-part name="day" form="numeric-leading-zeros" suffix="/"/>
    <date-part name="year"/>
  </date>
  <terms>
    <term name="and">and</term>
    <term name="and others">et al.</term>
    <term name="et-al">et al.</term>
    <term name="editor" form="short">ed.</term>
    <term name="editor" form="verb-short">ed.</term>
    <term name="editors" form="short">eds.</term>
    <term name="page" form="short">p.</term>
    <term name="pages" form="short">pp.</term>
    <term name="volume" form="short">vol.</term>
    <term name="issue" form="short">no.</term>
    <term name="accessed">accessed</term>
    <term name="retrieved">retrieved</term>
    <term name="from">from</term>
    <term name="in">in</term>
    <term name="forthcoming">forthcoming</term>
    <term name="no date" form="short">n.d.</term>
    <term name="anonymous">anonymous</term>
    <term name="anonymous" form="short">anon.</term>
    <term name="ibid">ibid</term>
    <term name="idem">idem</term>
    <term name="cited">op. cit.</term>
    <term name="open-quote">"</term>
    <term name="close-quote">"</term>
    <term name="open-inner-quote">'</term>
    <term name="close-inner-quote">'</term>
  </terms>
</locale>`;

const FR_FR_LOCALE = `<?xml version="1.0" encoding="utf-8"?>
<locale xmlns="http://purl.org/net/xbiblio/csl" version="1.0" xml:lang="fr-FR">
  <info>
    <rights license="http://creativecommons.org/licenses/by-sa/3.0/">CC BY-SA 3.0</rights>
    <updated>2023-01-01T00:00:00+00:00</updated>
  </info>
  <style-options punctuation-in-quote="false"/>
  <date form="text">
    <date-part name="day" suffix=" "/>
    <date-part name="month" suffix=" "/>
    <date-part name="year"/>
  </date>
  <date form="numeric">
    <date-part name="day" form="numeric-leading-zeros" suffix="/"/>
    <date-part name="month" form="numeric-leading-zeros" suffix="/"/>
    <date-part name="year"/>
  </date>
  <terms>
    <term name="and">et</term>
    <term name="and others">et al.</term>
    <term name="et-al">et al.</term>
    <term name="editor" form="short">éd.</term>
    <term name="editor" form="verb-short">éd.</term>
    <term name="editors" form="short">éds.</term>
    <term name="page" form="short">p.</term>
    <term name="pages" form="short">p.</term>
    <term name="volume" form="short">vol.</term>
    <term name="issue" form="short">n°</term>
    <term name="accessed">consulté le</term>
    <term name="retrieved">récupéré</term>
    <term name="from">de</term>
    <term name="in">dans</term>
    <term name="forthcoming">à paraître</term>
    <term name="no date" form="short">s.d.</term>
    <term name="anonymous">anonyme</term>
    <term name="anonymous" form="short">anon.</term>
    <term name="ibid">ibid</term>
    <term name="idem">idem</term>
    <term name="cited">op. cit.</term>
    <term name="open-quote">«&#160;</term>
    <term name="close-quote">&#160;»</term>
    <term name="open-inner-quote">"</term>
    <term name="close-inner-quote">"</term>
  </terms>
</locale>`;

// =============================================================================
// DEFAULT CSL STYLE (Chicago Author-Date)
// =============================================================================

const DEFAULT_CSL_STYLE = `<?xml version="1.0" encoding="utf-8"?>
<style xmlns="http://purl.org/net/xbiblio/csl" class="in-text" version="1.0" demote-non-dropping-particle="never">
  <info>
    <title>Chicago Manual of Style (Author-Date)</title>
    <id>http://www.zotero.org/styles/chicago-author-date</id>
    <updated>2023-01-01T00:00:00+00:00</updated>
  </info>
  <locale xml:lang="en">
    <terms>
      <term name="editor" form="verb-short">ed.</term>
      <term name="translator" form="verb-short">trans.</term>
    </terms>
  </locale>
  <macro name="author-short">
    <names variable="author">
      <name form="short" and="text" delimiter=", " initialize-with=". "/>
      <substitute>
        <names variable="editor"/>
        <names variable="translator"/>
        <text variable="title" form="short"/>
      </substitute>
    </names>
  </macro>
  <macro name="year-date">
    <choose>
      <if variable="issued">
        <date variable="issued">
          <date-part name="year"/>
        </date>
      </if>
      <else>
        <text term="no date" form="short"/>
      </else>
    </choose>
  </macro>
  <citation et-al-min="4" et-al-use-first="1" disambiguate-add-year-suffix="true" disambiguate-add-names="true" disambiguate-add-givenname="true">
    <sort>
      <key macro="author-short"/>
      <key macro="year-date"/>
    </sort>
    <layout prefix="(" suffix=")" delimiter="; ">
      <group delimiter=", ">
        <text macro="author-short"/>
        <text macro="year-date"/>
        <group>
          <label variable="locator" form="short"/>
          <text variable="locator" prefix=" "/>
        </group>
      </group>
    </layout>
  </citation>
  <bibliography hanging-indent="true" et-al-min="11" et-al-use-first="7">
    <sort>
      <key macro="author-short"/>
      <key macro="year-date"/>
    </sort>
    <layout suffix=".">
      <group delimiter=". ">
        <names variable="author">
          <name and="text" delimiter=", " delimiter-precedes-last="always" name-as-sort-order="first"/>
        </names>
        <text macro="year-date"/>
        <text variable="title"/>
      </group>
    </layout>
  </bibliography>
</style>`;

// =============================================================================
// ZOTERO TO CSL-JSON CONVERSION
// =============================================================================

/**
 * Map Zotero item type to CSL type
 */
function mapZoteroTypeToCsl(zoteroType: string): string {
  const typeMap: Record<string, string> = {
    'journalArticle': 'article-journal',
    'book': 'book',
    'bookSection': 'chapter',
    'magazineArticle': 'article-magazine',
    'newspaperArticle': 'article-newspaper',
    'thesis': 'thesis',
    'letter': 'personal_communication',
    'manuscript': 'manuscript',
    'interview': 'interview',
    'film': 'motion_picture',
    'artwork': 'graphic',
    'webpage': 'webpage',
    'report': 'report',
    'bill': 'bill',
    'case': 'legal_case',
    'hearing': 'hearing',
    'patent': 'patent',
    'statute': 'legislation',
    'email': 'personal_communication',
    'map': 'map',
    'blogPost': 'post-weblog',
    'instantMessage': 'personal_communication',
    'forumPost': 'post',
    'audioRecording': 'song',
    'videoRecording': 'motion_picture',
    'tvBroadcast': 'broadcast',
    'radioBroadcast': 'broadcast',
    'podcast': 'song',
    'computerProgram': 'software',
    'conferencePaper': 'paper-conference',
    'document': 'document',
    'encyclopediaArticle': 'entry-encyclopedia',
    'dictionaryEntry': 'entry-dictionary',
    'presentation': 'speech',
  };
  return typeMap[zoteroType] || 'document';
}

/**
 * Parse Zotero date string to CSL date
 */
function parseZoteroDate(dateStr: string | undefined): CSLDate | undefined {
  if (!dateStr) return undefined;

  // Try to parse ISO date or year
  const match = dateStr.match(/^(\d{4})(?:-(\d{2}))?(?:-(\d{2}))?/);
  if (match) {
    const parts: number[] = [parseInt(match[1], 10)];
    if (match[2]) parts.push(parseInt(match[2], 10));
    if (match[3]) parts.push(parseInt(match[3], 10));
    return { 'date-parts': [parts] };
  }

  // Fallback to raw
  return { raw: dateStr };
}

/**
 * Parse CSL variables from Zotero's extra field
 * Format: "variable-name: value" (one per line)
 *
 * Known CSL variables that can be in extra:
 * - original-date: 1867
 * - original-title: Original Title
 * - PMID: 12345678
 * - PMCID: PMC1234567
 */
function parseExtraField(extra: string | undefined): Record<string, string> {
  if (!extra) return {};

  const variables: Record<string, string> = {};
  const lines = extra.split('\n');

  for (const line of lines) {
    // Match "variable-name: value" pattern
    const match = line.match(/^([a-zA-Z][a-zA-Z0-9-]*)\s*:\s*(.+)$/);
    if (match) {
      const [, key, value] = match;
      variables[key.toLowerCase()] = value.trim();
    }
  }

  return variables;
}

/**
 * Convert Zotero creators to CSL names
 */
function convertCreators(
  creators: ZoteroItem['data']['creators'],
  creatorType: string
): CSLName[] | undefined {
  if (!creators) return undefined;

  const filtered = creators.filter((c) => c.creatorType === creatorType);
  if (filtered.length === 0) return undefined;

  return filtered.map((c) => {
    if (c.name) {
      return { literal: c.name };
    }
    return {
      family: c.lastName || '',
      given: c.firstName || '',
    };
  });
}

/**
 * Convert Zotero item to CSL-JSON format
 */
export function zoteroItemToCsl(item: ZoteroItem): CSLItem {
  const data = item.data;

  const cslItem: CSLItem = {
    id: item.key,
    type: mapZoteroTypeToCsl(data.itemType),
    title: data.title,
  };

  // Authors
  const authors = convertCreators(data.creators, 'author');
  if (authors) cslItem.author = authors;

  // Editors
  const editors = convertCreators(data.creators, 'editor');
  if (editors) cslItem.editor = editors;

  // Translators
  const translators = convertCreators(data.creators, 'translator');
  if (translators) cslItem.translator = translators;

  // Date
  const issued = parseZoteroDate(data.date);
  if (issued) cslItem.issued = issued;

  // Parse extra field for CSL variables not natively supported by Zotero
  const extraVars = parseExtraField(data.extra);

  // Original date (stored in extra field as "original-date: 1867")
  if (extraVars['original-date']) {
    const originalDate = parseZoteroDate(extraVars['original-date']);
    if (originalDate) cslItem['original-date'] = originalDate;
  }

  // Container title (journal, book title, etc.)
  if (data.publicationTitle) {
    cslItem['container-title'] = data.publicationTitle;
  }
  // For book sections, bookTitle is the container
  if (data.bookTitle) {
    cslItem['container-title'] = data.bookTitle;
  }

  // Publisher info
  if (data.publisher) cslItem.publisher = data.publisher;
  if (data.place) cslItem['publisher-place'] = data.place;

  // Volume, issue, pages
  if (data.volume) cslItem.volume = data.volume;
  if (data.issue) cslItem.issue = data.issue;
  if (data.pages) cslItem.page = data.pages;
  if (data.numPages) cslItem['number-of-pages'] = data.numPages;

  // Edition and collection/series
  if (data.edition) cslItem.edition = data.edition;
  if (data.series) cslItem['collection-title'] = data.series;
  if (data.seriesNumber) cslItem['collection-number'] = data.seriesNumber;

  // ISBN/ISSN
  if (data.ISBN) cslItem.ISBN = data.ISBN;
  if (data.ISSN) cslItem.ISSN = data.ISSN;

  // DOI and URL
  if (data.DOI) cslItem.DOI = data.DOI;
  if (data.url) cslItem.URL = data.url;

  // Abstract and notes
  if (data.abstractNote) cslItem.abstract = data.abstractNote;

  // Language
  if (data.language) cslItem.language = data.language;

  return cslItem;
}

// =============================================================================
// CITATION ENGINE
// =============================================================================

/** CSL Engine interface (citeproc-js) */
interface CSLEngine {
  updateItems(ids: string[]): void;
  updateUncitedItems(ids: string[]): void;
  makeCitationCluster(items: Array<{ id: string; locator?: string; label?: string }>): string;
  makeBibliography(): [object, string[]] | false;
  processCitationCluster(
    citation: {
      citationID: string;
      citationItems: Array<{ id: string; locator?: string; label?: string; prefix?: string; suffix?: string }>;
      properties: { noteIndex: number };
    },
    citationsPre: Array<[string, number]>,
    citationsPost: Array<[string, number]>
  ): [{ bibchange: boolean; citation_errors: string[] }, Array<[number, string]>];
}

/** Sys object required by citeproc-js */
interface CiteprocSys {
  retrieveLocale(lang: string): string;
  retrieveItem(id: string): CSLItem | null;
}

/**
 * Citation Service class
 */
class CitationService {
  private cslStyle: string = DEFAULT_CSL_STYLE;
  private items: Map<string, CSLItem> = new Map();
  private engine: CSLEngine | null = null;
  private CSL: typeof import('citeproc') | null = null;
  private initialized = false;
  private initPromise: Promise<void> | null = null;

  // Cache for pre-formatted citations (synchronous access)
  private citationCache: Map<string, string> = new Map();
  private cacheUpdateCallbacks: Set<() => void> = new Set();

  /**
   * Initialize the citation service
   */
  async init(): Promise<void> {
    if (this.initialized) return;
    if (this.initPromise) return this.initPromise;

    this.initPromise = this._doInit();
    return this.initPromise;
  }

  private async _doInit(): Promise<void> {
    try {
      // Dynamically import citeproc-js (CommonJS module)
      // Use default import for CommonJS compatibility
      const citeprocModule = await import('citeproc');
      // Handle both ESM default export and CommonJS module.exports
      this.CSL = citeprocModule.default || citeprocModule;
      this.initialized = true;
      log.info('Citation service initialized');
    } catch (error) {
      log.error('Failed to initialize citation service', { error });
      throw error;
    }
  }

  /**
   * Set the CSL style to use
   */
  setStyle(cslContent: string): void {
    log.info('setStyle called', {
      contentLength: cslContent?.length || 0,
      firstChars: cslContent?.substring(0, 100) || 'empty'
    });
    this.cslStyle = cslContent;
    this.engine = null; // Reset engine to use new style
    log.debug('CSL style updated');
    // Refresh cache with new style
    this.refreshCache();
  }

  /**
   * Register a callback to be called when citation cache is updated
   */
  onCacheUpdate(callback: () => void): () => void {
    this.cacheUpdateCallbacks.add(callback);
    return () => this.cacheUpdateCallbacks.delete(callback);
  }

  /**
   * Refresh the citation cache (pre-format all items)
   */
  async refreshCache(): Promise<void> {
    if (!this.initialized) {
      await this.init();
    }

    log.info('Refreshing citation cache', {
      itemCount: this.items.size,
      styleIsDefault: this.cslStyle === DEFAULT_CSL_STYLE,
      styleLength: this.cslStyle?.length || 0,
    });

    this.citationCache.clear();
    let successCount = 0;
    let fallbackCount = 0;

    for (const [key] of this.items) {
      try {
        const formatted = await this.formatCitation(key);
        this.citationCache.set(key, formatted);
        successCount++;
        log.debug('Citation cached successfully', { key, formatted: formatted.substring(0, 100) });
      } catch (error) {
        log.error('Failed to format citation via citeproc - this should not happen', { key, error });
        // Don't cache anything - let the UI show the raw [@key] format
        fallbackCount++;
      }
    }

    log.info('Citation cache refreshed', {
      total: this.citationCache.size,
      success: successCount,
      fallback: fallbackCount,
    });

    // Notify listeners
    for (const callback of this.cacheUpdateCallbacks) {
      try {
        callback();
      } catch (e) {
        log.error('Cache update callback failed', { error: e });
      }
    }
  }

  /**
   * Get cached formatted citation (synchronous)
   * Returns null if not cached - caller should handle this
   */
  getCachedCitation(key: string): string | null {
    const cached = this.citationCache.get(key);
    if (cached) {
      return cached;
    }
    log.debug('Citation not in cache yet', { key });
    return null;
  }

  /**
   * Check if a citation is cached and ready
   */
  isCitationCached(key: string): boolean {
    return this.citationCache.has(key);
  }

  /**
   * Get the current CSL style
   */
  getStyle(): string {
    return this.cslStyle;
  }

  /**
   * Reset to default style
   */
  resetStyle(): void {
    this.cslStyle = DEFAULT_CSL_STYLE;
    this.engine = null;
    log.debug('CSL style reset to default');
  }

  /**
   * Register a Zotero item for citation
   */
  registerItem(item: ZoteroItem): void {
    const cslItem = zoteroItemToCsl(item);
    this.items.set(item.key, cslItem);
    this.engine = null; // Reset engine when items change
    log.debug('Item registered', { key: item.key });
  }

  /**
   * Register multiple Zotero items (for browsing/insertion)
   * Skips items that are already registered to preserve local edits.
   * Use registerCSLItems() to force update items (e.g., from local bibliography).
   */
  registerItems(items: ZoteroItem[]): void {
    let newCount = 0;
    for (const item of items) {
      // Skip items already registered (preserve local edits)
      if (this.items.has(item.key)) {
        continue;
      }
      const cslItem = zoteroItemToCsl(item);
      this.items.set(item.key, cslItem);
      newCount++;
    }
    if (newCount > 0) {
      this.engine = null;
      log.debug('Items registered', { new: newCount, skipped: items.length - newCount });
      // Refresh cache with new items
      this.refreshCache();
    }
  }

  /**
   * Register pre-converted CSL-JSON items (from local bibliography)
   * Use this when items are already in CSL-JSON format
   */
  registerCSLItems(cslItems: CSLItem[]): void {
    for (const item of cslItems) {
      this.items.set(item.id, item);
    }
    this.engine = null;
    log.debug('CSL items registered', { count: cslItems.length });
    // Refresh cache with new items
    this.refreshCache();
  }

  /**
   * Register CSL items and wait for cache refresh (async version)
   * Use this when you need to ensure the cache is updated before continuing
   */
  async registerCSLItemsAsync(cslItems: CSLItem[]): Promise<void> {
    for (const item of cslItems) {
      this.items.set(item.id, item);
    }
    this.engine = null;
    log.debug('CSL items registered (async)', { count: cslItems.length });
    // Refresh cache and wait for completion
    await this.refreshCache();
  }

  /**
   * Register Zotero items in native format (converts to CSL on-the-fly)
   * This is the preferred method when working with items from bibliographyService
   */
  async registerZoteroDataItems(items: Array<Record<string, unknown>>): Promise<void> {
    // Import conversion function dynamically to avoid circular deps
    const { zoteroDataToCSL } = await import('./bibliographyService');

    for (const item of items) {
      const cslItem = zoteroDataToCSL(item as any);
      this.items.set(cslItem.id, cslItem);
    }
    this.engine = null;
    log.debug('Zotero data items registered', { count: items.length });
    // Refresh cache and wait for completion
    await this.refreshCache();
  }

  /**
   * Clear all registered items
   */
  clearItems(): void {
    this.items.clear();
    this.engine = null;
  }

  /**
   * Get a registered item by key
   */
  getItem(key: string): CSLItem | undefined {
    return this.items.get(key);
  }

  /**
   * Check if an item is registered
   */
  hasItem(key: string): boolean {
    return this.items.has(key);
  }

  /**
   * Create the citeproc engine
   */
  private createEngine(): CSLEngine {
    if (!this.CSL) {
      throw new Error('Citation service not initialized');
    }

    log.info('Creating citeproc engine', {
      styleLength: this.cslStyle?.length || 0,
      isDefault: this.cslStyle === DEFAULT_CSL_STYLE,
      stylePreview: this.cslStyle?.substring(0, 200) || 'empty'
    });

    const sys: CiteprocSys = {
      retrieveLocale: (lang: string) => {
        if (lang.startsWith('fr')) return FR_FR_LOCALE;
        return EN_US_LOCALE;
      },
      retrieveItem: (id: string) => {
        return this.items.get(id) || null;
      },
    };

    return new this.CSL.Engine(sys, this.cslStyle);
  }

  /**
   * Get or create the engine
   */
  private getEngine(): CSLEngine {
    if (!this.engine) {
      this.engine = this.createEngine();
      // Update items in engine
      this.engine.updateItems(Array.from(this.items.keys()));
    }
    return this.engine;
  }

  /**
   * Format a single citation using citeproc-js
   * Note: makeCitationCluster() is simpler but doesn't handle disambiguation.
   * For WYSIWYG preview, this is acceptable.
   */
  async formatCitation(
    itemKey: string,
    locator?: string,
    label?: string
  ): Promise<string> {
    await this.init();

    if (!this.items.has(itemKey)) {
      log.warn('Item not registered for citation', { key: itemKey });
      return `[@${itemKey}]`;
    }

    try {
      const engine = this.getEngine();
      const citeItems = [{ id: itemKey, locator, label }];
      const result = engine.makeCitationCluster(citeItems);
      log.debug('Citation formatted via citeproc', { key: itemKey, result: result.substring(0, 100) });
      return result;
    } catch (error) {
      log.error('Failed to format citation via citeproc', { key: itemKey, error });
      return `[@${itemKey}]`;
    }
  }

  /**
   * Format multiple citations together
   */
  async formatCitations(
    citations: Array<{ key: string; locator?: string; label?: string }>
  ): Promise<string> {
    await this.init();

    const validCitations = citations.filter((c) => this.items.has(c.key));
    if (validCitations.length === 0) {
      return citations.map((c) => `[@${c.key}]`).join('; ');
    }

    try {
      const engine = this.getEngine();
      const citeItems = validCitations.map((c) => ({
        id: c.key,
        locator: c.locator,
        label: c.label,
      }));
      return engine.makeCitationCluster(citeItems);
    } catch (error) {
      log.error('Failed to format citations', { error });
      return citations.map((c) => `[@${c.key}]`).join('; ');
    }
  }

  /**
   * Process all document citations in order for position-aware formatting
   * This enables ibid., op. cit., and other position-based citation styles.
   *
   * @param citations Array of citations in document order, each with:
   *   - id: unique identifier for this citation occurrence (e.g., "cite-1")
   *   - key: the item key (e.g., "durkheim1897")
   *   - noteIndex: position in document (1-based, e.g., footnote number)
   *   - locator: optional page/chapter reference
   *   - label: optional locator type (page, chapter, etc.)
   * @returns Map from citation id to formatted text
   */
  async processDocumentCitations(
    citations: Array<{
      id: string;
      key: string;
      noteIndex: number;
      locator?: string;
      label?: string;
      prefix?: string;
      suffix?: string;
    }>
  ): Promise<Map<string, string>> {
    await this.init();

    const results = new Map<string, string>();

    if (citations.length === 0) {
      return results;
    }

    // Filter to only valid citations (items must be registered)
    const validCitations = citations.filter(c => this.items.has(c.key));
    if (validCitations.length === 0) {
      // Return fallback for all citations
      for (const c of citations) {
        results.set(c.id, `[@${c.key}]`);
      }
      return results;
    }

    try {
      // Create a fresh engine for ordered processing
      // (processCitationCluster maintains state, so we need a clean start)
      const engine = this.createEngine();
      engine.updateItems(Array.from(this.items.keys()));

      // Process each citation in order
      const citationsPre: Array<[string, number]> = [];

      for (let i = 0; i < validCitations.length; i++) {
        const cit = validCitations[i];

        // Build the citation cluster
        const cluster = {
          citationID: cit.id,
          citationItems: [{
            id: cit.key,
            locator: cit.locator,
            label: cit.label,
            prefix: cit.prefix,
            suffix: cit.suffix,
          }],
          properties: {
            noteIndex: cit.noteIndex,
          },
        };

        // Process this citation cluster
        // Note: citationsPost must be empty when processing sequentially,
        // because those citations haven't been registered yet
        const [status, updateList] = engine.processCitationCluster(
          cluster,
          citationsPre,
          []  // No citationsPost - we process in order
        );

        console.log('[citation] processCitationCluster result:', {
          citationID: cit.id,
          key: cit.key,
          noteIndex: cit.noteIndex,
          citationsPre: citationsPre.map(([id, n]) => ({ id, noteIndex: n })),
          updateListLength: updateList.length,
          updateList: updateList.map(([idx, txt]) => ({ idx, text: txt })),
        });

        // updateList contains [index, formattedText] pairs where index is the
        // citation's position in document order (0-based).
        // Map these back to our citation IDs
        for (const [citIndex, formattedText] of updateList) {
          if (citIndex >= 0 && citIndex < validCitations.length) {
            const citId = validCitations[citIndex].id;
            results.set(citId, formattedText);
            console.log('[citation] Mapped result:', { citIndex, citId, text: formattedText });
          } else {
            console.warn('[citation] Index out of bounds:', { citIndex, validLength: validCitations.length });
          }
        }

        // Add this citation to the "pre" list for next iteration
        citationsPre.push([cit.id, cit.noteIndex]);
      }

      // Add fallback for any unregistered citations
      for (const c of citations) {
        if (!results.has(c.id)) {
          results.set(c.id, `[@${c.key}]`);
        }
      }

      console.log('[citation] Final results:', Array.from(results.entries()).map(([id, text]) => ({ id, text })));

      return results;

    } catch (error) {
      log.error('Failed to process document citations', { error });
      // Return fallback for all citations
      for (const c of citations) {
        results.set(c.id, `[@${c.key}]`);
      }
      return results;
    }
  }

  /**
   * Generate a full bibliography
   * Returns formatted HTML entries along with their item keys for editing
   */
  async makeBibliography(): Promise<{ entries: string[]; keys: string[] }> {
    await this.init();

    if (this.items.size === 0) {
      return { entries: [], keys: [] };
    }

    try {
      const engine = this.getEngine();
      const result = engine.makeBibliography();
      if (!result) return { entries: [], keys: [] };

      // result[0] contains metadata including entry_ids
      // result[1] contains the formatted HTML entries
      const metadata = result[0];
      const entries = result[1];

      // entry_ids is an array of arrays, each inner array contains the item key
      const keys = metadata.entry_ids?.map((ids: string[]) => ids[0]) || [];

      return { entries, keys };
    } catch (error) {
      log.error('Failed to generate bibliography', { error });
      return { entries: [], keys: [] };
    }
  }

  /**
   * Generate bibliography for specific item keys only
   * Used for rendering bibliography blocks with only cited items
   */
  async makeBibliographyForKeys(keys: string[]): Promise<{ entries: string[]; keys: string[] }> {
    await this.init();

    if (keys.length === 0) {
      return { entries: [], keys: [] };
    }

    // Filter to only registered items
    const validKeys = keys.filter(key => this.items.has(key));
    if (validKeys.length === 0) {
      return { entries: [], keys: [] };
    }

    try {
      // Create a fresh engine with only the requested items
      const engine = this.createEngine();
      // Use updateUncitedItems to include items in bibliography without citing them
      // This ensures makeBibliography() returns all requested items
      engine.updateUncitedItems(validKeys);

      const result = engine.makeBibliography();
      if (!result) return { entries: [], keys: [] };

      const metadata = result[0];
      const entries = result[1];
      const entryKeys = metadata.entry_ids?.map((ids: string[]) => ids[0]) || [];

      return { entries, keys: entryKeys };
    } catch (error) {
      log.error('Failed to generate bibliography for keys', { error, keys });
      return { entries: [], keys: [] };
    }
  }

}


// =============================================================================
// EXPORTS
// =============================================================================

/** Singleton instance */
export const citationService = new CitationService();

export default citationService;
